import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { playerAPI } from '../../services/api';
import { publicAssetUrl } from '../../utils/publicAssetUrl';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

const SearchWrap = styled.div`
  margin-bottom: 0.5rem;
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  &:focus-within {
    border-color: #043663;
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.12);
  }
`;

const SearchIcon = styled.span`
  color: #94a3b8;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  background: transparent;
  min-width: 0;
  &::placeholder {
    color: #94a3b8;
  }
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  background: ${(p) => (p.$selected ? 'rgba(4, 54, 99, 0.06)' : '#fff')};
  &:last-child {
    border-bottom: none;
  }
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e2e8f0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ResultMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultName = styled.div`
  font-weight: 700;
  color: #0f172a;
  font-size: 0.9rem;
  line-height: 1.3;
`;

const ResultSub = styled.div`
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 0.15rem;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SkillBadge = styled.span`
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  vertical-align: middle;
`;

const AddBtn = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(4, 54, 99, 0.25);
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SelectBtn = styled(AddBtn)`
  background: ${(p) =>
    p.$selected ? '#043663' : 'linear-gradient(135deg, #043663 0%, #d52b1e 100%)'};
`;

const LoadingHint = styled.div`
  font-size: 0.82rem;
  color: #64748b;
  padding: 0.65rem 0.25rem;
`;

const EmptyBox = styled.div`
  margin-top: 0.65rem;
  padding: 0.85rem 0.95rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const EmptyTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.35rem;
`;

const EmptyHint = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.45;
`;

const EmailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: stretch;
`;

const EmailInput = styled.input`
  flex: 1 1 180px;
  padding: 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
`;

const ActionBtn = styled.button`
  padding: 0.55rem 0.95rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SelfInfo = styled.div`
  margin-top: 0.65rem;
  padding: 0.85rem 0.95rem;
  border-radius: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 0.875rem;
  color: #92400e;
  line-height: 1.45;
`;

const ErrorMsg = styled.div`
  margin-top: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  font-size: 0.82rem;
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
`;

/**
 * Live player search for registration partner invite or team invite modal.
 *
 * @param {'registration'|'teams'} mode
 * @param {string} [teamId]
 * @param {boolean} [disabled]
 * @param {string} [selectedRowId]
 * @param {(partner: object) => void} [onAddPartner] registration: after skill-gap validation
 * @param {(row: object) => void} [onSelectRow] teams: select existing player
 * @param {(email: string) => void} [onInviteEmail] teams: invite non-HPS email
 */
export default function PlayerInviteSearch({
  mode = 'registration',
  teamId,
  disabled = false,
  selectedRowId,
  onAddPartner,
  onSelectRow,
  onInviteEmail,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  const { data: profileRes } = useQuery(
    'playerInviteSearchProfile',
    () => playerAPI.getProfile(),
    { staleTime: 5 * 60 * 1000, retry: false }
  );

  const me = profileRes?.data?.data || profileRes?.data || profileRes || {};
  const myEmail = String(me.email || '').trim().toLowerCase();
  const myId = me.id || me.playerId || null;

  const isSelfQuery = (q) => {
    const raw = String(q || '').trim();
    if (!raw) return false;
    if (myId && raw.toLowerCase() === String(myId).toLowerCase()) return true;
    if (myEmail && EMAIL_RE.test(raw) && raw.toLowerCase() === myEmail) return true;
    return false;
  };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    setErrorMsg(null);
  }, [debouncedQ]);

  const searchEnabled =
    !disabled && debouncedQ.length >= MIN_QUERY_LEN && (mode !== 'teams' || !!teamId);

  const searchParams = {
    q: debouncedQ,
    ...(teamId ? { teamId } : {}),
    ...(mode === 'registration' ? { context: 'registration' } : {}),
  };

  const { data, isFetching } = useQuery(
    ['playersSearch', mode, teamId || '', debouncedQ, mode === 'registration' ? 'reg' : 'def'],
    () => playerAPI.searchPlayers(searchParams),
    { enabled: searchEnabled, staleTime: 10 * 1000, keepPreviousData: true }
  );

  const results = Array.isArray(data?.data) ? data.data : [];
  const showEmpty =
    searchEnabled && !isFetching && debouncedQ.length >= MIN_QUERY_LEN && results.length === 0;
  const isSelfSearch = showEmpty && isSelfQuery(debouncedQ);
  const showInviteEmpty = showEmpty && !isSelfSearch;
  const queryLooksLikeEmail = EMAIL_RE.test(debouncedQ);

  const handleAddExisting = async (row) => {
    if (mode === 'teams') {
      onSelectRow?.(row);
      return;
    }
    setAddingId(row.id);
    setErrorMsg(null);
    try {
      const res = await playerAPI.lookupRegistrationPartner(row.id);
      const payload = res?.data || res;
      if (!payload?.found) {
        setErrorMsg(payload?.message || t('playerInviteSearch.notFound', 'Player not found'));
        return;
      }
      if (!payload.skillGapOk) {
        setErrorMsg(
          payload.skillGap?.message ||
            t(
              'registrationUpsell.skillBlocked',
              "You can't team up with this player — partners must be within 1.0 skill of each other."
            )
        );
        return;
      }
      const p = payload.player;
      const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
      onAddPartner?.({
        playerId: p.id,
        email: p.email,
        name: name || p.email,
        skillLevel: p.skillLevel,
        homeCanton: p.homeCanton || null,
        homeClub: p.homeClub || null,
        profilePictureUrl: p.profilePictureUrl || null,
        phoneNumber: p.phoneNumber || row.phoneNumber || null,
        createAccount: false,
      });
      setQuery('');
      setDebouncedQ('');
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e.message || t('playerInviteSearch.addFailed'));
    } finally {
      setAddingId(null);
    }
  };

  const submitEmailInvite = (rawEmail) => {
    const email = String(rawEmail || '').trim();
    if (!EMAIL_RE.test(email)) {
      setErrorMsg(t('playerInviteSearch.invalidEmail', 'Enter a valid email address'));
      return;
    }
    if (myEmail && email.toLowerCase() === myEmail) {
      setErrorMsg(
        t(
          'playerInviteSearch.cannotAddSelf',
          'This is your own account — you cannot add yourself as partner.'
        )
      );
      return;
    }
    setErrorMsg(null);
    if (mode === 'registration') {
      onAddPartner?.({ email, createAccount: true, name: email });
      setQuery('');
      setDebouncedQ('');
      setInviteEmail('');
    } else {
      onInviteEmail?.(email);
    }
  };

  return (
    <SearchWrap>
      <SearchField>
        <SearchIcon aria-hidden>
          <FaSearch />
        </SearchIcon>
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(
            'playerInviteSearch.placeholder',
            'Player name or email'
          )}
          disabled={disabled || (mode === 'teams' && !teamId)}
          autoComplete="off"
        />
      </SearchField>

      {isFetching && debouncedQ.length >= MIN_QUERY_LEN ? (
        <LoadingHint>{t('playerInviteSearch.searching', 'Searching players…')}</LoadingHint>
      ) : null}

      {results.length > 0 ? (
        <ResultsList role="listbox" aria-label={t('playerInviteSearch.resultsLabel', 'Player search results')}>
          {results.map((row) => {
            const img = row.profilePictureUrl ? publicAssetUrl(row.profilePictureUrl) : '';
            const name = row.fullName || [row.firstName, row.lastName].filter(Boolean).join(' ');
            const isSelected = selectedRowId && String(selectedRowId) === String(row.id);
            const metaParts = [row.email, row.phoneNumber].filter(Boolean);
            if (row.homeCanton) metaParts.unshift(row.homeCanton);
            return (
              <ResultRow key={row.id} $selected={isSelected} role="option" aria-selected={!!isSelected}>
                <Avatar>
                  {img ? <img src={img} alt="" /> : (name[0] || '?').toUpperCase()}
                </Avatar>
                <ResultMeta>
                  <ResultName>
                    {name}
                    {row.skillLevel != null ? (
                      <SkillBadge>
                        {t('registrationUpsell.skillLabel', 'Skill {{skill}}', {
                          skill: row.skillLevel,
                        })}
                      </SkillBadge>
                    ) : null}
                  </ResultName>
                  <ResultSub title={metaParts.join(' · ')}>{metaParts.join(' · ') || '—'}</ResultSub>
                </ResultMeta>
                {mode === 'teams' ? (
                  <SelectBtn
                    type="button"
                    $selected={isSelected}
                    disabled={disabled || addingId === row.id}
                    onClick={() => handleAddExisting(row)}
                    aria-label={t('playerInviteSearch.selectPlayer', 'Select {{name}}', { name })}
                  >
                    <FaPlus aria-hidden />
                  </SelectBtn>
                ) : (
                  <AddBtn
                    type="button"
                    disabled={disabled || addingId === row.id}
                    onClick={() => handleAddExisting(row)}
                    aria-label={t('playerInviteSearch.addPlayer', 'Add {{name}}', { name })}
                  >
                    <FaPlus aria-hidden />
                  </AddBtn>
                )}
              </ResultRow>
            );
          })}
        </ResultsList>
      ) : null}

      {isSelfSearch ? (
        <SelfInfo role="status">
          {t(
            'playerInviteSearch.cannotAddSelf',
            'This is your own account — you cannot add yourself as partner.'
          )}
        </SelfInfo>
      ) : null}

      {showInviteEmpty ? (
        <EmptyBox>
          <EmptyTitle>
            {t('playerInviteSearch.noResultsTitle', 'No HPS player found for “{{query}}”', {
              query: debouncedQ,
            })}
          </EmptyTitle>
          <EmptyHint>
            {t(
              'playerInviteSearch.noResultsHint',
              'If your teammate is not part of HPS yet, invite them by email. When they create an account with that email, they will receive your team invitation automatically.'
            )}
          </EmptyHint>
          <EmailRow>
            <EmailInput
              type="email"
              value={queryLooksLikeEmail ? debouncedQ : inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('playerInviteSearch.emailPlaceholder', 'Teammate email address')}
              disabled={disabled}
            />
            <ActionBtn
              type="button"
              disabled={disabled}
              onClick={() =>
                submitEmailInvite(queryLooksLikeEmail ? debouncedQ : inviteEmail)
              }
            >
              {mode === 'registration'
                ? t('playerInviteSearch.inviteToHps', 'Invite to HPS')
                : t('playerInviteSearch.inviteByEmail', 'Invite by email')}
            </ActionBtn>
          </EmailRow>
        </EmptyBox>
      ) : null}

      {errorMsg ? <ErrorMsg role="alert">{errorMsg}</ErrorMsg> : null}
    </SearchWrap>
  );
}
